import React from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types';

import { Link } from '~/components/common/link';
import { cn, contentfulLinkResolver } from '~/lib/utils';
import { Asset } from '~/server/generated/contentful-graphql';

import s from './contentful-rich-text.module.css';

const EmbedInstagram = dynamic(() => import('~/components/embeds/embed-instagram').then((mod) => mod.EmbedInstagram));
const EmbedTwitter = dynamic(() => import('~/components/embeds/embed-twitter').then((mod) => mod.EmbedTwitter));

export function isRichTextDocument(value: unknown): value is Document {
  if (typeof value !== 'object' || value === null) return false;
  if (!('nodeType' in value) || value.nodeType !== 'document') return false;
  if (!('content' in value) || !Array.isArray(value.content)) return false;
  return true;
}

const generateBody = (json: Document, links?: any) => {
  const linkFieldMap: { [index: string]: any } = {
    'entry-hyperlink': 'hyperlink',
    'asset-hyperlink': 'hyperlink',
    'embedded-entry-block': 'block',
    'embedded-asset-block': 'block',
  };

  const options: Options = {
    renderNode: {
      [INLINES.HYPERLINK]: (node, children) => {
        return <Link href={node.data.uri}>{children}</Link>;
      },
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        if (!links) return null;

        const linkField = linkFieldMap[node.nodeType];
        const targetId = node.data?.target?.sys?.id;
        const asset = links.assets[linkField]?.filter((cur: any) => cur.sys?.id === targetId)[0];
        if (!asset || !asset.__typename || !asset.url) return null;

        return <Link href={asset.url}>{children}</Link>;
      },
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        if (!links) return null;

        const linkField = linkFieldMap[node.nodeType];
        const targetId = node.data?.target?.sys?.id;
        const entry = links.entries[linkField].filter((cur: any) => cur.sys?.id === targetId)[0];

        if (!entry || !entry.__typename || !entry.slug) return null;

        const href = contentfulLinkResolver(entry.__typename, entry.slug);

        if (!href) return null;

        return <Link href={href}>{children}</Link>;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
        if (!links) return null;
        const linkField = linkFieldMap[node.nodeType];
        const targetId = node.data?.target?.sys?.id;
        const entry = links.entries[linkField].filter((cur: any) => cur?.sys?.id === targetId)[0];

        if (!entry || !entry.__typename) return null;
        switch (entry.__typename) {
          case 'EmbedInstagram':
            return <EmbedInstagram entry={entry} />;
          case 'EmbedTwitter':
            return <EmbedTwitter entry={entry} />;
          default:
            return null;
        }
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        if (!links) return null;

        const linkField = linkFieldMap[node.nodeType];
        const targetId = node.data?.target?.sys?.id;
        const asset: Asset = links.assets[linkField].filter((cur: any) => cur?.sys?.id === targetId)[0];

        // Images only
        const suppportedAssets = ['image/jpeg', 'image/png', 'image/gif'];

        if (
          !asset ||
          !asset.url ||
          !asset.width ||
          !asset.height ||
          !asset.title ||
          !asset.contentType ||
          !suppportedAssets.includes(asset.contentType)
        ) {
          return null;
        }

        const width = 700;
        const height = Math.round(width / (asset.width / asset.height));

        return (
          <div className='text-center'>
            <Image
              alt={asset.title || ''}
              className='mx-auto h-auto max-w-full'
              height={height}
              src={asset.url}
              width={width}
              quality={70}
            />
          </div>
        );
      },
    },
    renderText: (text) => text.split('\n').flatMap((text, i) => [i > 0 && <br key={`line-break-${i}`} />, text]),
  };

  return documentToReactComponents(json, options);
};

type ContentfulRichTextProps = {
  json: unknown;
  links?: any;
  dropCap?: boolean | null;
  visualStyle?: 'Base' | 'Default' | 'Centered' | 'Emphasized' | 'Columns' | 'None';
  className?: string;
};

export function ContentfulRichText({ json, links, visualStyle = 'Base', dropCap, className }: ContentfulRichTextProps) {
  const document = isRichTextDocument(json) ? json : null;
  const body = React.useMemo(() => (document ? generateBody(document, links) : null), [document, links]);

  if (!document) return null;

  return (
    <div
      className={cn(
        visualStyle !== 'None' && s.base,
        visualStyle === 'Default' && s.default,
        visualStyle === 'Centered' && `${s.default} text-center`,
        visualStyle === 'Emphasized' && s.emphasized,
        visualStyle === 'Columns' && s.columns,
        dropCap && s.dropcap,
        className,
      )}
    >
      {body}
    </div>
  );
}
